import { Box, Button, CheckBox, MaskedInput, RadioButtonGroup, ResponsiveContext, Text, Tip } from "grommet";
import { AddressSearchComponent } from "./address-search.component";
import { ReactComponent as LocationIcon } from '../assets/locationIcon.svg';
import { ReactComponent as RowCropIcon } from '../assets/rowCropIcon.svg';
import { ReactComponent as RangeAndPastureIcon } from '../assets/rangeAndPastureIcon.svg';
import { ReactComponent as TrashIcon } from '../assets/trashIcon.svg';
import { CircleInformation, FormDown, FormUp } from "grommet-icons";
import { forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import { getFontSize } from "./fast-quote.component";
import { transitions } from 'polished';
import { useFlags } from "launchdarkly-react-client-sdk";

export const LocationItemComponent = forwardRef(({ data, updateLocation, isSubmitted, setShowModal }: any, ref) => {

  const size = useContext(ResponsiveContext);
  const fontSize = getFontSize(size);
  const [searchInput, setSearchInput] = useState<string>(
    data.county ? `${data.county}, ${data.state}, United States` : '',
  );
  const [location, setLocation] = useState(data);
  const [address, setAddress] = useState<any>();
  const { hideNitrogenReduction } = useFlags();

  const [isRowCropAreaValid, setRowCropAreaValid] = useState(true);
  const [isPastureLandAreaValid, setPastureLandAreaValid] = useState(true);
  const [isLocationValid, setLocationValid] = useState(true);

  useEffect(() => {
    if (isSubmitted) {
      if (!location.state || !location.county) {
        setLocationValid(false);
      } else {
        setLocationValid(true);
      }
      if ((location.practices.reducedTillageNoTillage || location.practices.coverCropping || location.practices.nitrogenReduction || location.practices.legumeAddition) && !location.rowCropArea ||
        (!location.practices.reducedTillageNoTillage && !location.practices.coverCropping && !location.practices.nitrogenReduction && !location.practices.legumeAddition && location.rowCropArea)) {
        setRowCropAreaValid(false);
      } else {
        setRowCropAreaValid(true);
      }
      if ((location.practices.seeding || location.practices.fertilization || location.practices.improvedGrazing) && !location.pastureLandArea ||
        (!location.practices.seeding && !location.practices.fertilization && !location.practices.improvedGrazing && location.pastureLandArea)) {
        setPastureLandAreaValid(false);
      } else {
        setPastureLandAreaValid(true);
      }
      if (!location.rowCropArea && !location.pastureLandArea) {
        setPastureLandAreaValid(false);
        setRowCropAreaValid(false);
      }
    }
  }, [isSubmitted, location]);

  useImperativeHandle(ref, () => ({
    hasError() {
      return !isRowCropAreaValid || !isPastureLandAreaValid || !isLocationValid
    }
  }));

  useEffect(() => {
    updateLocation(location);
  }, [location]);


  useEffect(() => {
    if (address) {
      setLocation({
        ...location,
        state: address.state,
        county: address.county,
      });
    }
  }, [address]);

  const getLocationItemHeight = () => {
    switch (size) {
      case ('large'):
        return '26.5rem'
      case ('mobile'):
        return '38.75rem'
      default:
        return '29.5rem'
    }
  };

  return (
    <Box style={{ minHeight: 'unset' }}>
      <Box
        overflow={'hidden'}
        style={transitions('height 0.3s ease 0.1s')}
        className="hi-this-is-me"
      >
        <Box
          pad={'0.5rem'}
          style={{ minHeight: 'unset' }}
        >
          <Box
            direction='column'
            height={'fit-content'}
            background={'white'}
            style={{ minHeight: 'unset' }}
          >
            <Box
              direction={'column'}
              margin={'0 0 1rem 0'}
              style={{
                position: 'relative',
                minHeight: 'unset',
              }}
            >
              <Box
                direction={'row'}
                margin={'0.5rem 0'}
                align={"center"}
                style={{ minHeight: 'unset' }}
              >
                <Box height={'17px'}
                  width={'17px'}>
                  <LocationIcon />
                </Box>
                <Text
                  margin={'0 0.25rem'}
                  style={{ fontWeight: 600 }}
                  size={fontSize.small}
                >
                  Location
                </Text>
              </Box>
              <AddressSearchComponent
                initialValue={
                  location.county ? `${location.county}, ${location.state}, United States` : ''
                }
                hint={'Search for field/pasture county...'}
                border={{ color: !isLocationValid ? 'rgb(208, 52, 80)' : '#282828', size: '1px', style: 'solid' }}
                setSearchInput={setSearchInput}
                addressSelectedNameCallback={(result, stateShortCode) => {
                  const splitResult = typeof result === 'string' ? result.split(', ') : result('').split(', ');
                  setAddress({
                    state: stateShortCode,
                    county: splitResult[0],
                  });
                }}
                searchDataTypes={['district']}
              />
              {!isLocationValid && <Box
                style={{
                  color: '#d03450',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 800,
                  lineHeight: '16px'
                }}>
                Input a valid location
              </Box>}
            </Box>

            <Box
              direction={size === 'mobile' ? 'column' : 'row'}
              style={{ minHeight: 'unset', justifyContent: 'space-between' }}
            >
              <Box direction={'column'} style={{ minHeight: 'unset' }}>
                <Box
                  direction={'row'}
                  margin={'0 0 0.5rem 0'}
                  style={{ minHeight: 'unset' }}
                  align={"center"}
                >
                  <Box height={'16px'}
                    width={'16px'}>
                    <RowCropIcon />
                  </Box>
                  <Text
                    style={{ fontWeight: 600 }}
                    size={fontSize.small}
                    margin={'0 0.25rem'}
                  >
                    Row Crop Acres
                  </Text>
                </Box>
                <Box
                  border={{ color: !isRowCropAreaValid ? 'rgb(208, 52, 80)' : '#282828', size: '1px', style: 'solid' }}
                  width={'100%'}
                  height={'3rem'}
                  direction='row'
                  align='center'
                  style={{ minHeight: 'unset' }}
                >
                  <MaskedInput
                    mask={[
                      {
                        regexp: /^[0-9]*\.?[0-9]*$/,
                      },
                    ]}
                    style={{
                      fontWeight: 400,
                    }}
                    value={location.rowCropArea}
                    plain
                    size='1.25rem'
                    focusIndicator={false}
                    onChange={(event) => {
                      if(event.target.value!==''){
                        setLocation({ 
                          ...location, 
                          practices: {
                            ...location.practices,
                            reducedTillageNoTillage: true,
                            coverCropping: true,
                          },
                          rowCropArea: event.target.value ? Number(event.target.value) : '' 
                        });
                      }else{
                        setLocation({ ...location, rowCropArea: event.target.value ? Number(event.target.value) : '' });
                      }
                    }}
                    placeholder='e.g 1500'
                  />
                </Box>

                {/* Type of land */}
                <Box
                    direction={'column'}
                    style={{ minHeight: 'unset' }}
                    margin={'0 0 1rem 0'}
                  >
                    <Text size={fontSize.medium} weight={800} style={{margin: "20px 0 10px"}}>
                      Type of land:
                    </Text>
                    <RadioButtonGroup
                      direction={'column'}
                      margin={'0 0 0 0.5rem'}
                      name={'typeOfLand'}
                      value={location.typeOfLand}
                      onChange={(event) => {
                        console.log(location);
                        setLocation({ ...location, typeOfLand: event.target.value });
                      }}
                      options={[
                        {
                          label: (
                            <Text data-cy={'type-of-land-irrigated'} style={{
                              fontWeight: 700,
                              alignSelf: 'center',
                            }}
                            size='0.75rem' margin={'0 1rem 0 0'}>
                              Irrigated
                            </Text>
                          ),
                          value: 'irrigated',
                        },
                        {
                          label: (
                            <Text data-cy={'type-of-land-non-irrigated'} style={{
                              fontWeight: 700,
                              alignSelf: 'center',
                            }}
                            size='0.75rem' margin={'0 1rem 0 0'}>
                              Non-irrigated
                            </Text>
                          ),
                          value: 'nonIrrigated',
                        },
                      ]}
                    />
                  </Box>
                {/* Type of land END */}

                {
                  location.rowCropArea && (location.rowCropArea + location.pastureLandArea) < 500 && (
                    <Box
                      style={{
                        color: '#d03450',
                        fontSize: '12px',
                        fontStyle: 'normal',
                        fontWeight: 800,
                        lineHeight: '16px',
                        width: '225px'
                      }}>
                      Please note that the minimum acreage to enroll in our program is 500 acres.
                    </Box>
                  )
                }
                <Text size={fontSize.xSmall} color={'#808080'} style={{ marginTop: '3px' }} textAlign={'start'}>
                  Leave blank if not applicable
                </Text>
                <CheckBox
                  checked={location.practices.reducedTillageNoTillage}
                  disabled={location.practices.legumeAddition}
                  style={{
                    marginTop: '1rem',
                  }}
                  label={
                    <Text
                      style={{
                        fontWeight: 700,
                        marginTop: fontSize.xSmall,
                        alignSelf: 'center',
                      }}
                      size='0.75rem'
                    >
                      Reduced Tillage/No Tillage
                    </Text>
                  }
                  onChange={(event) => {
                    const practices = location.practices;
                    setLocation({
                      ...location,
                      practices: {
                        ...practices,
                        reducedTillageNoTillage: event.target.checked,
                        nitrogenReduction: (event.target.checked || practices.coverCropping) && location.practices.nitrogenReduction
                      }
                    });
                  }}
                />
                <CheckBox
                  onClick={
                    () => {
                      //If this checkbox is checked
                      if (!location.practices.coverCropping) {
                        //Show the modal
                        // setShowModal(true)
                      }
                    }
                  }
                  checked={location.practices.coverCropping}
                  disabled={location.practices.legumeAddition}
                  style={{
                    marginTop: '0.5rem',
                  }}
                  label={
                    <Text
                      style={{
                        fontWeight: 700,
                        marginTop: fontSize.xxSmall,
                        alignSelf: 'center',
                      }}
                      size='0.75rem'
                    >
                      Cover Cropping
                    </Text>
                  }
                  onChange={(event) => {
                    const practices = location.practices;
                    setLocation({
                      ...location,
                      practices: {
                        ...practices,
                        coverCropping: event.target.checked,
                        nitrogenReduction: (event.target.checked || practices.reducedTillageNoTillage) && location.practices.nitrogenReduction
                      }
                    });
                  }}
                />
                <Box direction={'row'} align={'center'} style={{ marginLeft: '10px' }}>
                  <CheckBox
                    checked={location.practices.legumeAddition}
                    disabled={!location.practices.coverCropping}
                    style={{
                      marginTop: '0.5rem',
                    }}
                    label={
                      <Text
                        style={{
                          fontWeight: 700,
                          marginTop: fontSize.xxSmall,
                          alignSelf: 'center',
                        }}
                        size='0.75rem'
                      >
                        Legume Addition
                      </Text>
                    }
                    onChange={(event) => {
                      const practices = location.practices;
                      setLocation({
                        ...location,
                        practices: {
                          ...practices,
                          legumeAddition: event.target.checked
                        }
                      });
                    }}
                  />
                  {!location.practices.coverCropping && (
                    <Tip
                      dropProps={{
                        align: { bottom: 'top' },
                      }}
                      content={
                        <Box
                          direction={'column'}
                          justify={'center'}
                          margin={'0 0 0.25rem 5.25rem'}
                        >
                          <Box
                            round={'0.25rem'}
                            pad={'8px 16px'}
                            height={'fit-content'}
                            width={'203px'}
                            justify={'center'}
                            background={{ color: '#282828' }}
                          >
                            <Text size={fontSize.small} color={'#FFFFFF'} textAlign="center">
                              This practice is an add-on to cover crops, please select "Cover Cropping" first, then select "Legume Addition"
                            </Text>
                          </Box>
                          <Box
                            margin={'0 0 0 1rem'}
                            style={{
                              width: 0,
                              height: 0,
                              borderLeft: '10px solid transparent',
                              borderRight: '10px solid transparent',
                              borderTop: '10px solid #282828',
                            }}
                          />
                        </Box>
                      }
                      plain={true}
                    >
                      <CircleInformation
                        style={{
                          margin: '0.5rem 0 0 0.375rem',
                          height: '20px',
                          width: '20px',
                        }}
                        color={'#282828'}
                      />
                    </Tip>
                  )}
                </Box>
                {!hideNitrogenReduction && <Box direction={'row'} align={'center'}>
                  <CheckBox
                    disabled={!location.practices.coverCropping && !location.practices.reducedTillageNoTillage && !location.practices.legumeAddition}
                    checked={location.practices.nitrogenReduction}
                    style={{
                      marginTop: '0.5rem',
                    }}
                    label={
                      <Text
                        style={{
                          fontWeight: 700,
                          marginTop: fontSize.xxSmall,
                          alignSelf: 'center',
                        }}
                        size='0.75rem'
                      >
                        Nitrogen Reduction
                      </Text>
                    }
                    onChange={(event) => {
                      const practices = location.practices;
                      setLocation({ ...location, practices: { ...practices, nitrogenReduction: event.target.checked } });
                    }}
                  />
                  {!location.practices.coverCropping && !location.practices.reducedTillageNoTillage && (
                    <Tip
                      dropProps={{
                        align: { bottom: 'top' },
                      }}
                      content={
                        <Box
                          direction={'column'}
                          justify={'center'}
                          margin={'0 0 0.25rem 5.25rem'}
                        >
                          <Box
                            round={'0.25rem'}
                            pad={'0.25rem 0.5rem'}
                            height={'fit-content'}
                            width={'8.5rem'}
                            justify={'center'}
                            background={{ color: '#282828' }}
                          >
                            <Text size={fontSize.small} color={'#FFFFFF'}>
                              You need to select another row crop practice first
                            </Text>
                          </Box>
                          <Box
                            margin={'0 0 0 1rem'}
                            style={{
                              width: 0,
                              height: 0,
                              borderLeft: '10px solid transparent',
                              borderRight: '10px solid transparent',
                              borderTop: '10px solid #282828',
                            }}
                          />
                        </Box>
                      }
                      plain={true}
                    >
                      <CircleInformation
                        style={{
                          margin: '0.5rem 0 0 0.375rem',
                          height: '20px',
                          width: '20px',
                        }}
                        color={'#282828'}
                      />
                    </Tip>
                  )}
                </Box>}

              </Box>
              <Box
                style={{
                  borderLeft: '1px solid #B4BFC6',
                  minHeight: 'unset',
                }}
                height={'96%'}
                margin={size === 'mobile' ? '0.5rem 0' : '0 1rem'}
              />
              <Box direction={'column'} style={{ minHeight: 'unset' }}>
                <Box
                  direction={'row'}
                  margin={'0 0 0.5rem 0'}
                  style={{ minHeight: 'unset' }}
                  align={"center"}
                >
                  <Box height={'16px'}
                    width={'16px'}>
                    <RangeAndPastureIcon />
                  </Box>
                  <Text
                    style={{ fontWeight: 600 }}
                    size={fontSize.small}
                    margin={'0 0.25rem'}
                  >
                    Range & Pasture Acres
                  </Text>
                </Box>
                <Box
                  border={{ color: !isPastureLandAreaValid ? 'rgb(208, 52, 80)' : '#282828', size: '1px', style: 'solid' }}
                  height={'3rem'}
                  width={'100%'}
                  direction='row'
                  align='center'
                  style={{ minHeight: 'unset' }}
                >
                  <MaskedInput
                    mask={[
                      {
                        regexp: /^[0-9]*\.?[0-9]*$/,
                      },
                    ]}
                    style={{
                      fontWeight: 400,
                    }}
                    value={location.pastureLandArea}
                    plain
                    size='1.25rem'
                    focusIndicator={false}
                    onChange={(event) => {
                      if(event.target.value!==''){
                        setLocation({ 
                          ...location, 
                          practices: {
                            ...location.practices,
                            improvedGrazing: true,
                            seeding: true,
                            fertilization: true,
                          },
                          pastureLandArea: event.target.value ? Number(event.target.value) : '' 
                        });
                      }else{
                        setLocation({ ...location, pastureLandArea: event.target.value ? Number(event.target.value) : '' });
                      }
                    }}
                    placeholder='e.g 1500'
                  />
                </Box>

                {/* Type of land */}
                <Box
                    direction={'column'}
                    style={{ minHeight: 'unset' }}
                    margin={'0 0 1rem 0'}
                  >
                    <Text size={fontSize.medium} weight={800} style={{margin: "20px 0 10px"}}>
                      Type of land:
                    </Text>
                    <RadioButtonGroup
                      direction={'column'}
                      margin={'0 0 0 0.5rem'}
                      name={'typeOfLandRange'}
                      value={location.typeOfLandRange}
                      onChange={(event) => {
                        console.log(location);
                        setLocation({ ...location, typeOfLandRange: event.target.value });
                      }}
                      options={[
                        {
                          label: (
                            <Text data-cy={'type-of-land-irrigated'} style={{
                              fontWeight: 700,
                              alignSelf: 'center',
                            }}
                            size='0.75rem' margin={'0 1rem 0 0'}>
                              Irrigated
                            </Text>
                          ),
                          value: 'irrigated',
                        },
                        {
                          label: (
                            <Text data-cy={'type-of-land-non-irrigated'} style={{
                              fontWeight: 700,
                              alignSelf: 'center',
                            }}
                            size='0.75rem' margin={'0 1rem 0 0'}>
                              Non-irrigated
                            </Text>
                          ),
                          value: 'nonIrrigated',
                        },
                      ]}
                    />
                  </Box>
                {/* Type of land END */}

                {
                  location.pastureLandArea && (location.rowCropArea + location.pastureLandArea) < 500 && (
                    <Box
                      style={{
                        color: '#d03450',
                        fontSize: '12px',
                        fontStyle: 'normal',
                        fontWeight: 800,
                        lineHeight: '16px',
                        width: '225px'
                      }}>
                      Please note that the minimum acreage to enroll in our program is 500 acres.
                    </Box>
                  )
                }
                <Text size={fontSize.xSmall} color={'#808080'} style={{ marginTop: '3px' }} textAlign={'start'}>
                  Leave blank if not applicable
                </Text>
                <CheckBox
                  checked={location.practices.improvedGrazing}
                  style={{
                    marginTop: '1rem',
                  }}
                  label={
                    <Text
                      style={{
                        fontWeight: 700,
                        marginTop: '0.75rem',
                        alignSelf: 'center',
                      }}
                      size='0.75rem'
                    >
                      Improved Grazing
                    </Text>
                  }
                  onChange={(event) => {
                    const practices = location.practices;
                    setLocation({ ...location, practices: { ...practices, improvedGrazing: event.target.checked } });
                  }}
                />
                <CheckBox
                  checked={location.practices.seeding}
                  style={{
                    marginTop: '0.5rem',
                  }}
                  label={
                    <Text
                      style={{
                        fontWeight: 700,
                        marginTop: '0.5rem',
                        alignSelf: 'center',
                      }}
                      size='0.75rem'
                    >
                      Seeding
                    </Text>
                  }
                  onChange={(event) => {
                    const practices = location.practices;
                    setLocation({ ...location, practices: { ...practices, seeding: event.target.checked } });
                  }}
                />
                <CheckBox
                  checked={location.practices.fertilization}
                  style={{
                    marginTop: '0.5rem',
                  }}
                  label={
                    <Text
                      style={{
                        fontWeight: 700,
                        marginTop: '0.5rem',
                        alignSelf: 'center',
                      }}
                      size='0.75rem'
                    >
                      Fertilization
                    </Text>
                  }
                  onChange={(event) => {
                    const practices = location.practices;
                    setLocation({ ...location, practices: { ...practices, fertilization: event.target.checked } });
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box >
  )
});